import { useCallback } from "react"

import { createFilterOptions } from "@mui/material/Autocomplete"
import { useFormikContext } from "formik"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { SearchIcon } from "@l2r-front/l2r-icons"

import { I18N_NAMESPACE } from "../../constants/i18n"

import * as Styled from "./TagsAutocomplete.styled"

const TAG_MAX_CHARS = 25

export interface AutocompleteOption {
    inputValue?: string
    title?: string
}

export type TagOption = string | AutocompleteOption

interface FilterOptionsParams {
    inputValue: string
}

type TagsAutocompleteProps = {
    className?: string
    name: string
    variant: string
    label?: string
    tags: string[]
    existingTags: string[]
    onChange?: (_tagsValues: string[]) => void
}

export const TagsAutocomplete = (props: TagsAutocompleteProps) => {
    const {
        className,
        label,
        existingTags = [],
        name,
        onChange,
        tags = [],
        variant = "outlined",
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setFieldValue } = useFormikContext()

    const filter = createFilterOptions<TagOption>({
        trim: true,
    })

    const setValue = useCallback(
        (fieldName: string, newValue: TagOption[]) => {
            const tagsValues = newValue.map((value: TagOption) => {
                if (typeof value !== "string" && value?.inputValue) {
                    return value.inputValue
                }
                return value as string
            })

            setFieldValue(fieldName, tagsValues)
            onChange?.(tagsValues)
        },
        [setFieldValue, onChange],
    )

    return (
        <Styled.Autocomplete
            id="tags-select"
            className={className}
            label={label || t(I18N_NAMESPACE, "containers.tagsAutocomplete.label")}
            options={existingTags as TagOption[]}
            noOptionsText={t(I18N_NAMESPACE, "containers.tagsAutocomplete.noOption")}
            defaultValue={tags as TagOption[]}
            name={name}
            popupIcon={<SearchIcon />}
            disableClearable
            setFieldValue={
                setValue as (_fieldName: string, _newValue: TagOption[]) => void
            }
            value={tags as TagOption[]}
            variant={variant}
            filterSelectedOptions
            filterOptions={(
                options: TagOption[],
                params: FilterOptionsParams,
            ): TagOption[] => {
                const filtered = filter(options, {
                    inputValue: params.inputValue,
                    getOptionLabel: (option: TagOption) => {
                        return typeof option === "string" ? option : option.title ?? ""
                    },
                }) as TagOption[]

                const inputValue = params.inputValue?.trim()

                const isExistingInOptions = options.some((option) => {
                    const optionValue =
                        typeof option === "string"
                            ? option.toLowerCase()
                            : option.inputValue?.toLowerCase()

                    return inputValue.toLowerCase() === optionValue
                })

                const isExistingInTags = tags.some((tag) => {
                    return inputValue.toLowerCase() === tag.toLowerCase()
                })

                if (
                    inputValue !== "" &&
                    !isExistingInOptions &&
                    !isExistingInTags
                ) {
                    filtered.push({
                        inputValue,
                        title: `${t(
                            I18N_NAMESPACE,
                            "containers.tagsAutocomplete.add",
                        )} "${inputValue}"`,
                    })
                }

                return filtered
            }}
            getOptionLabel={(option: TagOption): string => {
                if (typeof option === "string") {
                    return option
                }

                return option.title ?? ""
            }}
            isOptionEqualToValue={(
                option: TagOption,
                value: string,
            ): boolean => {
                const optionValue =
                    typeof option === "string"
                        ? option.toLowerCase()
                        : option.inputValue?.toLowerCase()

                return value.toLowerCase() === optionValue
            }}
            maxLength={TAG_MAX_CHARS}
        />
    )
}